import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, Phone, Calendar, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { getMemberById, getMemberTransactions, formatINR, MemberStatus } from '../../data/members';
function statusClasses(status: MemberStatus): string {
  switch (status) {
    case 'Active':
      return 'bg-green-50 text-green-700 border border-green-200';
    case 'Pending':
      return 'bg-orange-50 text-orange-700 border border-orange-200';
    case 'Suspended':
      return 'bg-red-50 text-red-700 border border-red-200';
    default:
      return 'bg-club-canvasAlt text-club-ink/70 border border-club-border';
  }
}
export function AdminMemberDetail() {
  const {
    id
  } = useParams();
  const member = getMemberById(id);
  if (!member) {
    return <div className="max-w-3xl mx-auto text-center py-20" data-id="element-455">
        <AlertCircle size={40} className="mx-auto text-club-ink/30 mb-4" data-id="element-456" />
        <h1 className="text-2xl font-serif text-club-green mb-2" data-id="element-457">
          Member not found
        </h1>
        <p className="text-club-ink/60 mb-6" data-id="element-458">
          We couldn't find a member with ID “{id}”.
        </p>
        <Link to="/admin/members" className="btn-primary px-5 py-2.5" data-id="element-459">
          Back to Members
        </Link>
      </div>;
  }
  const transactions = getMemberTransactions(member);
  return <div className="max-w-5xl mx-auto space-y-6" data-id="element-460">
      <Link to="/admin/members" className="inline-flex items-center gap-2 text-sm text-club-ink/60 hover:text-club-green transition-colors" data-id="element-461">
        <ArrowLeft size={16} data-id="element-462" /> Back to Members
      </Link>

      {/* Profile header */}
      <motion.div initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4
    }} className="bg-white border border-club-border rounded-sm p-6 flex flex-col sm:flex-row sm:items-center gap-5" data-id="element-463">
        <div className="h-16 w-16 rounded-full bg-club-green text-club-gold flex items-center justify-center font-serif text-2xl shrink-0" data-id="element-464">
          {member.name.charAt(0)}
        </div>
        <div className="flex-1" data-id="element-465">
          <h1 className="text-2xl font-serif text-club-green" data-id="element-466">{member.name}</h1>
          <p className="text-sm text-club-ink/50 font-mono" data-id="element-467">{member.id}</p>
        </div>
        <div className="flex flex-col items-start sm:items-end gap-2" data-id="element-468">
          <span className={`inline-flex px-3 py-1 rounded-sm text-xs font-medium ${statusClasses(member.status)}`} data-id="element-469">
            {member.status}
          </span>
          <span className="text-sm text-club-gold font-medium" data-id="element-470">
            {member.tier}
          </span>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-471">
        {/* Contact + balance */}
        <div className="space-y-6" data-id="element-472">
          <div className="bg-white border border-club-border rounded-sm p-6" data-id="element-473">
            <h2 className="text-lg font-serif text-club-green mb-4" data-id="element-474">Contact</h2>
            <ul className="space-y-3 text-sm" data-id="element-475">
              <li className="flex items-center gap-3 text-club-ink/80" data-id="element-476">
                <Mail size={16} className="text-club-gold shrink-0" data-id="element-477" />
                <span className="truncate" data-id="element-478">{member.email}</span>
              </li>
              <li className="flex items-center gap-3 text-club-ink/80" data-id="element-479">
                <Phone size={16} className="text-club-gold shrink-0" data-id="element-480" />
                {member.phone}
              </li>
              <li className="flex items-center gap-3 text-club-ink/80" data-id="element-481">
                <Calendar size={16} className="text-club-gold shrink-0" data-id="element-482" />
                Joined {member.joinedDate}
              </li>
              <li className="flex items-center gap-3 text-club-ink/80" data-id="element-483">
                <Clock size={16} className="text-club-gold shrink-0" data-id="element-484" />
                Last visit {member.lastVisit}
              </li>
            </ul>
          </div>

          <div className={`rounded-sm p-6 ${member.outstandingBalance > 0 ? 'bg-club-burgundy text-white' : 'bg-club-green text-white'}`} data-id="element-485">
            <p className="text-xs uppercase tracking-wider opacity-80 mb-1" data-id="element-486">
              Outstanding Balance
            </p>
            <p className="text-3xl font-serif text-club-gold" data-id="element-487">
              {formatINR(member.outstandingBalance)}
            </p>
            <p className="text-xs opacity-70 mt-2" data-id="element-488">
              {member.outstandingBalance > 0 ? 'Dues pending collection' : 'All dues cleared'}
            </p>
          </div>
        </div>

        {/* Transactions */}
        <div className="lg:col-span-2 bg-white border border-club-border rounded-sm overflow-hidden" data-id="element-489">
          <div className="p-6 border-b border-club-border bg-club-canvas/30" data-id="element-490">
            <h2 className="text-lg font-serif text-club-green" data-id="element-491">
              Transaction History
            </h2>
          </div>
          <div className="divide-y divide-club-border" data-id="element-492">
            {transactions.map(tx => <div key={tx.id} className="px-6 py-4 flex items-center justify-between gap-4" data-id="element-493">
                <div data-id="element-494">
                  <p className="font-medium text-club-ink" data-id="element-495">{tx.title}</p>
                  <p className="text-xs text-club-ink/50" data-id="element-496">{tx.date}</p>
                </div>
                <div className="flex items-center gap-4" data-id="element-497">
                  <span className="font-medium text-club-green" data-id="element-498">
                    {formatINR(tx.amount)}
                  </span>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-medium ${tx.status === 'Paid' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-orange-50 text-orange-700 border border-orange-200'}`} data-id="element-499">
                    {tx.status === 'Paid' ? <CheckCircle size={12} data-id="element-500" /> : <Clock size={12} data-id="element-501" />}
                    {tx.status}
                  </span>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}